import React, { useState, useEffect } from 'react';
import { Star, ShoppingCart, Minus, Plus, Check, ShieldCheck, Truck } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useStore, getEffectivePrice } from '../context/StoreContext';
import { CURRENCY } from '../mock/mock';
import { Bubbles, SparkleAccents, glowClass } from './Decor';
import { toast } from 'sonner';
import { useMagnifier } from './Effects';
import ZoomViewer, { isTouchDevice } from './ZoomViewer';

const ProductModal = ({ product, open, onClose }) => {
  const { settings, addToCart } = useStore();
  const [qty, setQty] = useState(1);
  const { imgRef, lensEl, handlers } = useMagnifier(product?.image, 2.8, 170);
  const [zoomOpen, setZoomOpen] = useState(false);
  const touch = isTouchDevice();

  useEffect(() => { if (open) setQty(1); }, [open, product]);

  if (!product) return null;
  const { finalPrice, discountPercent, hasDiscount } = getEffectivePrice(product, settings);
  const outOfStock = product.stock <= 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden gap-0">
        <div className="grid md:grid-cols-2">
          <div {...(touch ? {} : handlers)} onClick={() => touch && setZoomOpen(true)} data-testid="product-modal-zoom-area" className="relative overflow-hidden flex items-center justify-center p-8 min-h-[260px] cursor-zoom-in">
            <div className={`absolute inset-0 ${glowClass(product)}`} />
            <div className="absolute inset-0 bg-secondary/20" />
            <Bubbles count={7} />
            <SparkleAccents />
            {hasDiscount && (
              <Badge className="absolute top-4 left-4 z-20 bg-[#e11d1d] text-white hover:bg-[#e11d1d]">-{discountPercent}%</Badge>
            )}
            <div className="product-shadow-ellipse absolute bottom-6 left-1/2 -translate-x-1/2 h-5 w-36" />
            {lensEl}
            <img ref={imgRef} src={product.image} alt={product.name} className="relative z-10 h-64 md:h-80 w-auto object-contain drop-shadow-[0_18px_22px_rgba(0,0,0,0.2)]" />
          </div>

          <div className="p-6 md:p-8">
            <Badge variant="outline" className="border-primary/30 text-primary mb-2">{product.category}</Badge>
            <DialogTitle className="font-display font-extrabold text-2xl text-foreground">{product.name}</DialogTitle>
            <p className="text-primary font-medium">{product.tagline}</p>

            <div className="flex items-center gap-1.5 mt-2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className={`h-4 w-4 ${i < Math.round(product.rating) ? 'fill-[#f5b400] text-[#f5b400]' : 'text-gray-300'}`} />
              ))}
              <span className="text-sm text-muted-foreground ml-1">{product.rating} · {product.reviewsCount} reviews</span>
            </div>

            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{product.description}</p>

            <ul className="mt-4 space-y-2">
              {product.features?.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-foreground/80">
                  <Check className="h-4 w-4 text-primary" /> {f}
                </li>
              ))}
            </ul>

            <div className="mt-5 flex items-baseline gap-3">
              <span className="font-display font-extrabold text-3xl text-foreground">{CURRENCY}{finalPrice}</span>
              {hasDiscount && <span className="text-muted-foreground line-through">{CURRENCY}{product.price}</span>}
              <Badge variant="outline" className="border-primary/30 text-primary">{product.size} pack</Badge>
            </div>

            {!outOfStock ? (
              <div className="mt-5 flex items-center gap-3">
                <div className="flex items-center border border-border rounded-full">
                  <button className="p-2.5 hover:text-primary" onClick={() => setQty((q) => Math.max(1, q - 1))}><Minus className="h-4 w-4" /></button>
                  <span className="w-8 text-center font-semibold">{qty}</span>
                  <button className="p-2.5 hover:text-primary" onClick={() => setQty((q) => Math.min(product.stock, q + 1))}><Plus className="h-4 w-4" /></button>
                </div>
                <Button
                  onClick={() => { addToCart(product, qty); toast.success(`${qty} \u00d7 ${product.name} added to cart`); onClose(); }}
                  data-testid="product-modal-add-to-cart" className="btn-shimmer flex-1 bg-primary hover:bg-primary/90 text-white font-semibold py-6"
                >
                  <ShoppingCart className="h-4 w-4 mr-2" /> Add to Cart
                </Button>
              </div>
            ) : (
              <Button disabled className="mt-5 w-full py-6">Out of Stock</Button>
            )}

            <div className="mt-5 flex items-center gap-5 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-primary" /> Genuine product</span>
              <span className="flex items-center gap-1.5"><Truck className="h-4 w-4 text-primary" /> Cash on Delivery</span>
            </div>
          </div>
        </div>
      </DialogContent>
      <ZoomViewer src={product.image} alt={product.name} open={zoomOpen} onClose={() => setZoomOpen(false)} />
    </Dialog>
  );
};

export default ProductModal;
